/**
 * Prev / next controls for the cart-drawer upsell row.
 *
 * The row is a horizontal scroll container, which on a touch screen needs nothing:
 * you swipe it. On a desktop with a mouse it is unreachable — there is no visible
 * scrollbar (an overlay scrollbar on macOS appears only while already scrolling, so
 * it cannot advertise anything), and a wheel scrolls the page, not the row. The
 * cards past the fold were simply unreachable for those customers.
 *
 * ── Why this is its own module, loaded from cart-drawer.liquid ────────────────
 *
 * The upsell markup lives inside `[data-hydration-key="cart-drawer-inner"]`, which
 * `morphSection(..., { mode: 'hydration' })` replaces on every cart change — and a
 * `<script>` inserted by a DOM morph never executes (the lesson paid for in
 * cart-addon-guard.liquid). So the tag sits in `cart-drawer.liquid` OUTSIDE that
 * element, where it runs once; the element it defines then upgrades every time the
 * row is morphed back in.
 *
 * No imports, so it needs nothing from the theme's import map.
 */
class CartUpsellsScroller extends HTMLElement {
  /** @type {HTMLElement | null} */ #scroller = null;
  /** @type {HTMLElement | null} */ #prev = null;
  /** @type {HTMLElement | null} */ #next = null;
  /** @type {ResizeObserver | null} */ #observer = null;
  /** @type {number | null} */ #frame = null;

  connectedCallback() {
    this.#scroller = this.querySelector('[data-upsell-scroller]');
    this.#prev = this.querySelector('[data-upsell-nav="prev"]');
    this.#next = this.querySelector('[data-upsell-nav="next"]');
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#prev?.addEventListener('click', this.#goPrev);
    this.#next?.addEventListener('click', this.#goNext);

    // Observe the LIST as well as the scroller, and schedule rather than measure
    // straight away. The drawer is a `<dialog>`, so until it opens the scroller has
    // no box at all; the first callback then arrives with the viewport already at
    // its final 479px but the list not yet laid out, so scrollWidth still equals
    // clientWidth and the buttons conclude there is nothing to scroll — and nothing
    // ever re-measures. Watching the list catches the frame its real width lands.
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(this.#scroller);
    const list = this.#scroller.querySelector('ul');
    if (list) this.#observer.observe(list);

    // A dialog that is opened and closed does not necessarily resize anything.
    this.closest('dialog')?.addEventListener('toggle', this.#schedule);

    this.#schedule();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#prev?.removeEventListener('click', this.#goPrev);
    this.#next?.removeEventListener('click', this.#goNext);
    this.#observer?.disconnect();
    this.closest('dialog')?.removeEventListener('toggle', this.#schedule);
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
  }

  /** Coalesces every trigger — scroll, resize, dialog toggle — into one measure
      per frame, after layout has settled. */
  #schedule = () => {
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #onScroll = () => this.#schedule();

  /** One card plus its gap, so a click advances by exactly one snap position. */
  get #step() {
    const card = this.#scroller?.querySelector('li');
    if (!card) return this.#scroller?.clientWidth ?? 0;
    const list = card.parentElement;
    const gap = list ? parseFloat(getComputedStyle(list).columnGap) || 0 : 0;
    return card.getBoundingClientRect().width + gap;
  }

  #goPrev = () => this.#scrollBy(-1);
  #goNext = () => this.#scrollBy(1);

  /** @param {number} direction */
  #scrollBy(direction) {
    this.#scroller?.scrollBy({ left: direction * this.#step, behavior: 'smooth' });
  }

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    // `scrollLeft` is negative in a right-to-left writing mode, and fractional at
    // fractional zoom levels, so compare distances rather than exact positions.
    const position = Math.abs(scroller.scrollLeft);
    const overflow = scroller.scrollWidth - scroller.clientWidth;
    const TOLERANCE = 2;

    const scrollable = overflow > TOLERANCE;
    if (this.#prev) this.#prev.hidden = !scrollable || position <= TOLERANCE;
    if (this.#next) this.#next.hidden = !scrollable || position >= overflow - TOLERANCE;
  };
}

if (!customElements.get('cart-upsells-scroller')) {
  customElements.define('cart-upsells-scroller', CartUpsellsScroller);
}
